import { useEffect, useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { listsApi, type ShoppingListSummary } from '../lib/domain';
import { useScope } from '../lib/hooks';

// Der Einkaufsmodus meldet seinen Fortschritt per Event an die Kopfzeile.
export const SHOPPING_PROGRESS_EVENT = 'shopping-progress';
export interface ShoppingProgress {
  listId: string;
  name: string;
  pending: number;
  total: number;
}

// Schnellzugriff: "Zum Einkauf" öffnet ein Dropdown aller offenen Listen
// und springt direkt in den Einkaufsmodus der gewählten Liste.
export function ShopMenu() {
  const [scope] = useScope();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState<ShoppingListSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const shopMatch = useMatch('/lists/:id/shop');
  const shopId = shopMatch?.params.id;
  const [progress, setProgress] = useState<ShoppingProgress | null>(null);

  useEffect(() => {
    function onProgress(e: Event) {
      setProgress((e as CustomEvent<ShoppingProgress>).detail);
    }
    window.addEventListener(SHOPPING_PROGRESS_EVENT, onProgress);
    return () => window.removeEventListener(SHOPPING_PROGRESS_EVENT, onProgress);
  }, []);

  useEffect(() => {
    if (!open || !scope) return;
    let cancelled = false;
    setLoading(true);
    listsApi
      .list(scope, 'OPEN')
      .then((r) => {
        if (!cancelled) setLists(r.data.lists.filter((l) => !l.memoScopeKey)); // Merkliste hat keinen Einkaufsmodus
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, scope]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {shopId ? (
        <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
          {progress && progress.listId === shopId && (
            <span role="status" style={{ whiteSpace: 'nowrap' }}>
              {progress.pending} von {progress.total} Produkten offen
            </span>
          )}
          <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
            {progress && progress.listId === shopId ? progress.name : 'Einkaufsliste'} ▾
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
          🛍️ Zum Einkauf
        </button>
      )}
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 4,
            minWidth: 220,
            maxHeight: '60vh',
            overflowY: 'auto',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {loading && lists.length === 0 && <span className="muted" style={{ padding: 12 }}>Lädt…</span>}
          {!loading && lists.length === 0 && <span className="muted" style={{ padding: 12 }}>Keine offenen Listen</span>}
          {lists.map((l) => (
            <button
              key={l.id}
              type="button"
              role="menuitem"
              style={{ textAlign: 'left', border: 'none', borderRadius: 0, background: 'none', padding: '10px 12px' }}
              onClick={() => {
                setOpen(false);
                navigate(`/lists/${l.id}/shop`);
              }}
            >
              {l.name ?? l.storeBranch?.name ?? "Einkaufsliste"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
